/**
 * Structured location picker (auto + immo). Looks like one search field,
 * but every suggestion is a structured entity (city | region |
 * postal_code) from the public locations endpoint — typed text is NEVER
 * used as a filter. While text is entered without a pick, the field says
 * so and offers to clear; the search view blocks the search until the
 * user picked or cleared. An empty field keeps the country-wide search.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, MapPin, X } from "lucide-react";
import { getPublicLocations } from "../../api/publicApi";

const TYPE_KEY = {
  city: "hero.locationCity",
  district: "hero.locationDistrict",
  region: "hero.locationRegion",
  postal_code: "hero.locationPlz",
};

export default function LocationPicker({
  value,
  onChange,
  country,
  vertical = "immo",
  testid = "hero-location",
  text: textProp,
  onTextChange,
}) {
  const { t } = useTranslation("discover");
  const controlled = typeof onTextChange === "function";
  const [ownText, setOwnText] = useState("");
  const text = controlled ? textProp || "" : ownText;
  const setText = (v) => (controlled ? onTextChange(v) : setOwnText(v));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const boxRef = useRef(null);
  const genRef = useRef(0);
  // A pick unmounts the suggestion and mounts the "selected" pill in the
  // SAME rectangle — the follow-up click of that very same interaction
  // would otherwise land on the clear-X and wipe the fresh selection.
  const pickedAtRef = useRef(0);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const term = text.trim();
  useEffect(() => {
    if (value || term.length < 2) {
      setItems([]);
      setOpen(false);
      setBusy(false);
      setFailed(false);
      return undefined;
    }
    const gen = ++genRef.current;
    setBusy(true);
    setFailed(false);
    const h = setTimeout(async () => {
      try {
        const res = await getPublicLocations(term, { country: country || undefined, vertical });
        if (genRef.current !== gen) return;
        setItems(res.items || []);
        setFailed(false);
        setOpen(true);
      } catch {
        if (genRef.current !== gen) return;
        setItems([]);
        setFailed(true);
        setOpen(true);
      } finally {
        if (genRef.current === gen) setBusy(false);
      }
    }, 250);
    return () => clearTimeout(h);
  }, [term, country, vertical, value, nonce]);

  const pick = (it) => {
    pickedAtRef.current = Date.now();
    onChange(it);
    setText("");
    setItems([]);
    setOpen(false);
  };

  const clearSelection = () => {
    if (Date.now() - pickedAtRef.current < 500) return;
    onChange(null);
    setText("");
  };

  if (value) {
    return (
      <div
        data-testid={`${testid}-selected`}
        className="flex w-full items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-sm text-emerald-200"
      >
        <MapPin size={13} className="shrink-0 text-emerald-400" aria-hidden />
        <span className="truncate">{value.label}</span>
        {!!value.type && (
          <span className="shrink-0 rounded bg-zinc-900/60 px-1.5 py-0.5 text-[10px] text-zinc-400">
            {t(TYPE_KEY[value.type] || "hero.locationCity")}
          </span>
        )}
        <button
          type="button"
          data-testid={`${testid}-clear`}
          onClick={clearSelection}
          className="ml-auto shrink-0 rounded p-0.5 text-zinc-400 hover:text-zinc-100"
        >
          <X size={13} aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-xl border bg-zinc-900 px-3 ${
          term ? "border-amber-500/60" : "border-zinc-700"
        }`}
      >
        <MapPin size={13} className="shrink-0 text-zinc-500" aria-hidden />
        <input
          data-testid={testid}
          className="w-full bg-transparent py-2.5 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none"
          placeholder={t("hero.locationPickerPh")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
        />
        {busy && <Loader2 size={13} className="shrink-0 animate-spin text-zinc-500" aria-hidden />}
        {!busy && !!term && (
          <button
            type="button"
            data-testid={`${testid}-clear-input`}
            onClick={() => setText("")}
            className="shrink-0 rounded p-0.5 text-zinc-400 hover:text-zinc-100"
            aria-label={t("search.locationClearInput")}
          >
            <X size={13} aria-hidden />
          </button>
        )}
      </div>

      {!!term && (
        <div
          data-testid={`${testid}-pending`}
          className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-300"
        >
          <AlertCircle size={11} className="shrink-0" aria-hidden />
          <span>{t("search.locationPickHint")}</span>
        </div>
      )}

      {open && (
        <div
          data-testid={`${testid}-suggestions`}
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 py-1 shadow-xl shadow-black/40"
        >
          {failed ? (
            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-amber-300">
              <span>{t("search.optionsError")}</span>
              <button
                type="button"
                data-testid={`${testid}-retry`}
                onClick={() => setNonce((n) => n + 1)}
                className="rounded-md border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-200 hover:border-zinc-500"
              >
                {t("search.retry")}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-xs text-zinc-600">{t("search.locationNoResults")}</div>
          ) : (
            items.map((it, i) => (
              <button
                key={`${it.type}-${it.value}-${i}`}
                type="button"
                data-testid={`${testid}-opt-${it.type}-${it.value}`}
                onClick={() => pick(it)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
              >
                <span className="truncate">{it.label}</span>
                {it.sublabel && it.sublabel !== it.label && (
                  <span className="truncate text-[11px] text-zinc-500">{it.sublabel}</span>
                )}
                <span className="ml-auto shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-500">
                  {t(TYPE_KEY[it.type] || "hero.locationCity")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
